import * as React from 'react';
import {
  Image,
  makeStyles,
  mergeClasses,
  Persona,
  shorthands,
  Spinner,
  Subtitle1,
  Text,
  tokens,
} from '@fluentui/react-components';
import { EditFilled } from '@fluentui/react-icons';
import { defineMessage, useIntl } from 'react-intl';
import type { UserAccount } from '@foundershub/api-client';
import { TextLink } from '@foundershub/components';
import disconnectedDogSvg from '../media/disconnected-dog.svg';
import { ContentCard, ContentCardHeader } from './ContentCard';

interface Props {
  unsortedMembers?: Array<UserAccount>;
  isLoading?: boolean;
  me: boolean;
  userEmail: string;
  displayLinkedIn?: boolean;
}

export function TeamMembers({ unsortedMembers, isLoading, me, userEmail, displayLinkedIn }: Props) {
  const intl = useIntl();
  const classes = useStyles();

  const teamMembers = React.useMemo(() => {
    return unsortedMembers
      ? [...unsortedMembers].sort((a, b) => (a.email === userEmail ? -1 : b.email === userEmail ? 1 : 0))
      : [];
  }, [unsortedMembers]);

  return (
    <ContentCard>
      <ContentCardHeader
        header={<Subtitle1>{intl.formatMessage(teamMembersLabel)}</Subtitle1>}
        action={
          me && teamMembers?.length >= 1 ? (
            <TextLink title="Edit" as="a" aria-label={intl.formatMessage(editLabel)} href="/settings/team">
              <EditFilled className={classes.editIcon} />
            </TextLink>
          ) : null
        }
      />

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {teamMembers.map((member, index) => (
            <div
              key={index}
              className={mergeClasses(classes.member, member.email === userEmail && classes.memberCurrentUser)}
            >
              <Persona
                name={`${member.first_name!} ${member.last_name!}`}
                secondaryText={displayLinkedIn ? member.linkedin_profile_url : member.contact_email || member.email}
                avatar={{ image: { src: member.photo }, size: 72, color: 'colorful' }}
              />
            </div>
          ))}
          {teamMembers.length === 1 ? <AddMembers /> : null}
        </>
      )}
    </ContentCard>
  );
}

function AddMembers() {
  const intl = useIntl();
  const classes = useStyles();
  return (
    <div className={classes.addMembers}>
      <Image src={disconnectedDogSvg} alt="" className={classes.noMembersImage} />
      <Text>
        {intl.formatMessage(addMembersText, {
          link: function Link(str) {
            // @ts-ignore formatjs/formatjs#3707
            return (<TextLink href="/settings/team">{str}</TextLink>) as string;
          },
        })}
      </Text>
    </div>
  );
}

const editLabel = defineMessage({
  defaultMessage: 'Edit',
  description: 'link to edit company information',
});
const teamMembersLabel = defineMessage({
  defaultMessage: 'Team Members',
  description: 'list of company team members',
});

const addMembersText = defineMessage({
  defaultMessage:
    'You have not yet added a team member. <link>Invite your teammates</link> to join you and get access to all the benefits and features of Microsoft for Startups.',
  description: 'add team member prompt',
});

const useStyles = makeStyles({
  member: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: tokens.spacingHorizontalXL,
    ...shorthands.padding(tokens.spacingVerticalL, 0),
    width: '100%',
  },
  memberCurrentUser: {
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
  },
  addMembers: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  noMembersImage: {
    maxWidth: '170px',
    aspectRatio: 1,
    alignSelf: 'center',
    justifySelf: 'center',
  },
  editIcon: {
    height: '20px',
    width: '20px',
  },
});
